import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, Eye, EyeOff } from 'lucide-react';

export const LoginPage = () => {
    const { login } = useAuth();
    const { t, language, toggleLanguage } = useLanguage();
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email || !password) {
            toast.error(language === 'id' ? 'Mohon isi semua field' : 'Please fill all fields');
            return;
        }

        setLoading(true);
        try {
            await login(email, password);
            toast.success(language === 'id' ? 'Login berhasil!' : 'Login successful!');
            navigate('/dashboard');
        } catch (error) {
            const message = error.response?.data?.detail || (language === 'id' ? 'Login gagal' : 'Login failed');
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col relative overflow-hidden">
            {/* Background Image with Overlay */}
            <div 
                className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                style={{ 
                    backgroundImage: 'url(/assets/background-logo.jpg)',
                    filter: 'blur(2px)'
                }}
            />
            <div className="absolute inset-0 bg-gradient-to-br from-slate-900/85 via-slate-800/80 to-red-900/70" />
            
            {/* Content */}
            <div className="relative z-10 min-h-screen flex flex-col">
                {/* Header */}
                <header className="p-4 flex justify-between items-center">
                    <Link to="/" className="flex items-center gap-3">
                        <img 
                            src="/assets/logo.jpg" 
                            alt="Sekolah NusaTraders" 
                            className="w-12 h-12 rounded-lg object-contain shadow-lg"
                        />
                        <div>
                            <span className="font-heading font-bold text-lg text-white block leading-tight drop-shadow-lg">SEKOLAH</span>
                            <span className="font-heading font-bold text-sm text-red-400 block leading-tight drop-shadow-lg">NUSATRADERS</span>
                        </div>
                    </Link>
                    <div className="lang-toggle" data-testid="lang-toggle">
                        <button
                            onClick={() => language !== 'id' && toggleLanguage()}
                            className={language === 'id' ? 'active' : ''}
                        >
                            ID
                        </button>
                        <button
                            onClick={() => language !== 'en' && toggleLanguage()}
                            className={language === 'en' ? 'active' : ''}
                        >
                            EN
                        </button>
                    </div>
                </header>

                {/* Main Content */}
                <main className="flex-1 flex items-center justify-center p-4">
                    <Card className="w-full max-w-md shadow-2xl border-0 bg-white/95 backdrop-blur-sm">
                    <CardHeader className="text-center">
                        <CardTitle className="text-2xl font-heading">
                            {language === 'id' ? 'Selamat Datang Kembali' : 'Welcome Back'}
                        </CardTitle>
                        <CardDescription>
                            {language === 'id' ? 'Masuk ke akun VVIP Anda' : 'Login to your VVIP account'}
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="email">{t('email')}</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="email@example.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    disabled={loading}
                                    data-testid="login-email"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="password">{t('password')}</Label>
                                <div className="relative">
                                    <Input
                                        id="password"
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="••••••••"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        disabled={loading}
                                        data-testid="login-password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                    >
                                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>
                            <Button
                                type="submit"
                                className="w-full"
                                disabled={loading}
                                data-testid="login-submit-btn"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        {t('loading')}
                                    </>
                                ) : (
                                    t('login')
                                )}
                            </Button>
                        </form>

                        <div className="mt-6 text-center text-sm">
                            <span className="text-slate-500">{t('noAccount')}</span>{' '}
                            <Link to="/register" className="text-primary hover:underline font-medium" data-testid="register-link">
                                {t('register')}
                            </Link>
                        </div>

                        <div className="mt-4 pt-4 border-t border-slate-100 text-center">
                            <Link 
                                to="/admin/login" 
                                className="text-sm text-slate-400 hover:text-slate-600"
                                data-testid="admin-login-link"
                            >
                                {language === 'id' ? 'Login sebagai Admin' : 'Login as Admin'}
                            </Link>
                        </div>
                    </CardContent>
                    </Card>
                </main>
            </div>
        </div>
    );
};
